import React from 'react'
import { compact } from 'lodash'

import PlainText from '../UI/PlainText'
import { monetize } from '../../lib/formats'
import { LogoHeader, ProjectSummary, QuoteFooter } from './PrintableQuoteHeaders'

import './styles/actuals.css'

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- actual: an actual object
- project: a project attribute INCLUDING:
  agency, client, user and agencyProducer
- print: Boolean: adapt actual to printing (default: false)
*/
const InternalActual = props => {
  const { actual, project, print } = props
  const agency = project.included('agency')
  const client = project.included('client')
  const user = project.included('user')
  const agencyProducer = project.included('agencyProducer')
  const projectTypeAddress = project.attributes.projectTypeAddress
//  const docketNumber = project.id;
  const docketNumber = project.attributes.formattedDocketNo;
  const summary = actual.attributes.formattedSummary
  const categories = summary.categories

  // Sorting categories properly for display
  let orderedCategoryKeys = []
  for (let categoryName in categories) {
    let index = Number(categories[categoryName].order)
    // Avoid overwriting items - we may have services with the same order.
    // In this case, keep adding 1 until we're not overwriting anything.
    // NOTE: Possibly an ugly hack that may bite me one day?
    while (orderedCategoryKeys[index]) {
      index += 1
    }
    orderedCategoryKeys[index] = categoryName
  }
  // Close gaps in array to make sure nothing blows up
  orderedCategoryKeys = compact(orderedCategoryKeys)

  const cssClassList = ['printable-actual']
  if (!print) cssClassList.push('internal-actual-screen')
  const cssClasses = cssClassList.join(' ')

  return (
    <section className={cssClasses}>
      <LogoHeader
        title="ACCOUNTING ACTUALS"
        docketNumber={docketNumber}
      />

      <ProjectSummary
        project={project}
        actual={actual}
        agency={agency}
        client={client}
        agencyProducer={agencyProducer}
        user={user}
      />

      <table className="actual-table">
        <thead>
          <tr>
            <th className="text-header">CATEGORY</th>
            <th className="text-header">
              SERVICE
            </th>
            <th colSpan={3} className="span-header thick-left thick-top">
              ESTIMATE
            </th>
            <th
              colSpan={6}
              className="span-header thick-left thick-top thick-right"
            >
              ACTUALS
            </th>
          </tr>
          <tr>
            <th colSpan={2} className="span-header" />
            <th className="number-header thick-left">Cost<br/>total</th>
            <th className="number-header">Rate<br/>Total</th>
            <th className="number-header">Margin</th>
            <th className="number-header thick-left">Qty</th>
            <th className="number-header">Cost</th>
            <th className="number-header">Cost<br/>Total</th>
            <th className="number-header">Rate</th>
            <th className="number-header">Rate<br/>Total</th>
            <th className="number-header thick-right">Margin</th>
          </tr>
        </thead>

        {orderedCategoryKeys.map(category => (
          <tbody key={`tbody-${category}`}>
            {categories[category].lineItems
              .sort((a, b) => a.order - b.order)
              .map((lineItem, index) => (
                <tr
                  key={`tr-${category}-item-${index}`}
                  className={index === 0 ? 'top-row' : ''}
                >
                  <td className="category-column">
                    {index === 0 ? category : ''}
                  </td>

                  <td className="service-column">
                    <PlainText
                      className="service-description"
                      text={lineItem.description}
                    />
                  </td>

                  <td className="number-column thick-left dark-shaded">
                    {lineItem.formattedTotalCost}
                  </td>

                  <td className="number-column shaded">
                    {lineItem.formattedTotal}
                  </td>

                  <td className="number-column dark-shaded">
                    {lineItem.formattedTotalMargin}
                  </td>

                  <td className="number-column thick-left shaded">
                    {lineItem.actualNumber}
                  </td>

                  <td className="number-column dark-shaded">
                    {lineItem.formattedActualCost}
                  </td>

                  <td className="number-column shaded">
                    {lineItem.formattedActualTotalCost}
                  </td>

                  <td className="number-column dark-shaded">
                    {lineItem.formattedActualRate}
                  </td>

                  <td className="number-column shaded">
                    {lineItem.formattedActualTotal}
                  </td>

                  <td className="number-column thick-right dark-shaded">
                    {lineItem.formattedActualTotalMargin}
                  </td>
                </tr>
              ))}

            <tr key={`tr-${category}-total`}>
              <th colSpan={2} className="total-header">
                {category} totals
              </th>

              <td className="category-total-price thick-left dark-shaded">
                {categories[category].formattedCostSubTotal}
              </td>

              <td className="category-total-price shaded">
                {categories[category].formattedSubTotal}
              </td>

              <td className="category-total-price dark-shaded">
                {categories[category].formattedMarginSubTotal}
              </td>

              <td className="thick-left category-total-price shaded" />

              <td className="category-total-price dark-shaded"/>

              <td className="category-total-price shaded">
                {categories[category].formattedActualCostSubTotal}
              </td>

              <td className="category-total-price dark-shaded" />

              <td className="category-total-price shaded">
                {categories[category].formattedActualSubTotal}
              </td>

              <td className="category-total-price thick-right dark-shaded">
                {categories[category].formattedActualMarginSubTotal}
              </td>
            </tr>

            <tr key={`tr-${category}-total-sep`} className="tr-separator">
              <td colSpan={11} />
            </tr>
          </tbody>
        ))}

        <tbody>
          {actual.attributes.notes && (
            <tr>
              <td colSpan={11} className="shaded">
                Notes:
                <PlainText text={actual.attributes.notes} />
              </td>
            </tr>
          )}
          <tr className="tr-separator">
            <td colSpan={11} />
          </tr>

          <tr>
            <th />
            <th className="total-header">
              Totals (before taxes)
            </th>
            <td className="category-total-price thick-left dark-shaded">
              {actual.attributes.formattedGrossCost}
            </td>
            <td className="category-total-price shaded">
              {actual.attributes.formattedGrossTotal}
            </td>
            <td className="category-total-price dark-shaded">
              {monetize(actual.attributes.grossTotal - actual.attributes.grossCost)}
            </td>
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded">
              {actual.attributes.formattedActualGrossCost}
            </td>
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded">
              {actual.attributes.formattedActualGrossTotal}
            </td>
            <td className="category-total-price thick-right dark-shaded">
              {monetize(actual.attributes.actualGrossTotal - actual.attributes.actualGrossCost)}
            </td>
          </tr>

          <tr>
            <th />
            <th className="total-header">
              Taxes ({actual.attributes.taxCode})
            </th>
            <td className="category-total-price thick-left dark-shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded">
              {actual.attributes.formattedActualTaxes}
            </td>
            <td className="category-total-price thick-right dark-shaded" />
          </tr>

          <tr>
            <th />
            <th className="total-header">
              Grand total
            </th>
            <td className="category-total-price thick-left dark-shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price dark-shaded" />
            <td className="category-total-price shaded">
              {actual.attributes.formattedActualGrandTotal}
            </td>
            <td className="category-total-price thick-right dark-shaded" />
          </tr>
        </tbody>
      </table>
      <QuoteFooter projectTypeAddress={projectTypeAddress} />
    </section>
  )
}

export default InternalActual



// WEBPACK FOOTER //
// ./src/components/Quotes/InternalActual.jsx